import { useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FileText, Link as LinkIcon, X, Paperclip, Plus } from 'lucide-react';

/**
 * SupportingEvidence — shared Drive-link chip widget used by Client Approvals
 * and Collections rows. Renders existing evidence as clickable chips and
 * exposes an "Add link" popover.
 *
 * Props:
 *   entity     — 'client-approvals' | 'collections' (the API resource path)
 *   parentId   — the approval/collection id
 *   items      — current attachments array
 *   canEdit    — role gate; when false renders read-only chips
 *   currentUserEmail — used to show a delete affordance only on items you added
 *   isOwner    — Owner can delete anyone's evidence
 *   onChange   — fired after add/remove with the fresh evidence list (best-effort;
 *                page can just re-fetch on any change)
 */
export function SupportingEvidence({ entity, parentId, items = [], canEdit,
                                     currentUserEmail, isOwner, onChange }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('quotation');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!url.trim() || !title.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/${entity}/${parentId}/attachments`,
                               { drive_url: url.trim(), title: title.trim(), doc_type: docType });
      toast.success('Evidence attached');
      setUrl(''); setTitle(''); setOpen(false);
      onChange?.([...(items || []), r.data]);
    } catch (e) {
      toast.error(parseErr(e));
    } finally { setBusy(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Remove "${ev.title}" from this record?`)) return;
    try {
      await api.delete(`/${entity}/${parentId}/attachments/${ev.id}`);
      toast.success('Evidence removed');
      onChange?.((items || []).filter(x => x.id !== ev.id));
    } catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid={`evidence-${parentId}`}>
      {(items || []).map(ev => {
        const canRemove = isOwner || ev.added_by === currentUserEmail;
        return (
          <span
            key={ev.id}
            className="inline-flex items-center gap-1 px-2 py-[3px] text-[11px] rounded-full border bg-[hsl(var(--surface-1))] hover:bg-[hsl(var(--surface-2))] transition-colors"
            style={{ borderColor: 'hsl(var(--border))' }}
            data-testid={`evidence-chip-${ev.id}`}
            title={`${ev.doc_type} · added by ${ev.added_by} · ${(ev.added_at || '').slice(0, 10)}`}
          >
            <a href={ev.drive_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 max-w-[180px] truncate"
               data-testid={`evidence-open-${ev.id}`}>
              <FileText size={11} className="flex-none" />
              <span className="truncate">{ev.title}</span>
              <LinkIcon size={9} className="flex-none opacity-70" />
            </a>
            {canEdit && canRemove && (
              <button onClick={() => remove(ev)}
                      className="ml-0.5 text-[hsl(var(--foreground-subtle))] hover:text-[hsl(var(--danger))]"
                      data-testid={`evidence-remove-${ev.id}`}
                      title="Remove attachment"
              >
                <X size={11} />
              </button>
            )}
          </span>
        );
      })}
      {canEdit && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              className="inline-flex items-center gap-1 px-2 py-[3px] text-[11px] rounded-full border border-dashed text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]"
              style={{ borderColor: 'hsl(var(--border))' }}
              data-testid={`evidence-add-${parentId}`}
              title="Attach a Google Drive, Dropbox, or OneDrive link"
            >
              <Paperclip size={11} />Attach link
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-3 w-[320px]" side="bottom" align="start"
                          data-testid={`evidence-add-popover-${parentId}`}>
            <div className="space-y-2">
              <div className="text-[12px] font-semibold flex items-center gap-1">
                <Paperclip size={12} />Attach supporting evidence
              </div>
              <p className="text-[11px] text-[hsl(var(--foreground-subtle))] leading-snug">
                Paste a Google&nbsp;Drive, Dropbox, or OneDrive link. Existing access rules apply — the app never uploads or copies the file.
              </p>
              <label className="block">
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">Drive URL</span>
                <Input value={url} onChange={e => setUrl(e.target.value)}
                       placeholder="https://drive.google.com/…"
                       className="mt-1 h-8 text-[13px]"
                       data-testid={`evidence-url-${parentId}`}
                       autoFocus />
              </label>
              <label className="block">
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">Title</span>
                <Input value={title} onChange={e => setTitle(e.target.value)}
                       placeholder="e.g. Signed quote"
                       className="mt-1 h-8 text-[13px]"
                       data-testid={`evidence-title-${parentId}`} />
              </label>
              <label className="block">
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">Document type</span>
                <select value={docType} onChange={e => setDocType(e.target.value)}
                        className="mt-1 w-full h-8 border rounded-md px-2 text-[13px]"
                        style={{ borderColor: 'hsl(var(--border))' }}
                        data-testid={`evidence-doctype-${parentId}`}>
                  <option value="email">Email</option>
                  <option value="email_pdf">Email PDF</option>
                  <option value="quotation">Quotation</option>
                  <option value="design_deliverable">Design deliverable</option>
                  <option value="proforma_invoice">Proforma invoice</option>
                  <option value="payment_proof">Payment proof</option>
                  <option value="call_summary">Call summary</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <div className="flex justify-end gap-1 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}
                        disabled={busy}>Cancel</Button>
                <Button size="sm" onClick={submit}
                        disabled={busy || !url.trim() || !title.trim()}
                        data-testid={`evidence-submit-${parentId}`}>
                  <Plus size={12} className="mr-1" />{busy ? 'Adding…' : 'Attach'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
